import {
  type DashboardEditCall,
  type DashboardToolResultSummary,
  resolveDashboardEdit,
} from '@server/dashboard-spec-patch';

import type { SSEMessage } from '@/_shared/types';
import {
  type DashboardArtifact,
  type DashboardArtifactSnapshot,
  isArtifactContentEmpty,
  type SavedDashboardRef,
} from '@/chat/hooks/useChatDashboardArtifacts';
import type { DashboardSpec } from '@/dashboard/types';

export const DASHBOARD_PREVIEW_TOOL = 'mcp__dashboard-draft__preview_dashboard';
export const DASHBOARD_UPDATE_TOOL = 'mcp__dashboard-draft__update_dashboard';
export const DASHBOARD_SAVE_TOOL = 'mcp__dashboard__save_dashboard';

export type DashboardArtifactEvent = 'preview_updated' | 'saved' | 'draft_restored';

/** Passed when opening a transcript card — hydrates saved dashboards from DB on click. */
export interface DashboardArtifactOpenHint {
  title?: string;
  saved?: boolean;
  dashboardId?: string;
}

export interface DashboardArtifactUiMetadata {
  slug: string;
  title: string;
  event: DashboardArtifactEvent;
  revision?: number;
}

function parseSpecJson(spec: unknown): DashboardSpec | null {
  if (typeof spec !== 'string') return null;
  try {
    const parsed = JSON.parse(spec) as DashboardSpec;
    if (!parsed || !Array.isArray(parsed.panels)) return null;
    return {
      ...parsed,
      parameters: Array.isArray(parsed.parameters) ? parsed.parameters : [],
    };
  } catch {
    return null;
  }
}

export function previewArgsToDraft(args: unknown): DashboardArtifactSnapshot | null {
  if (!args || typeof args !== 'object') return null;
  const a = args as Record<string, unknown>;
  const title = a['title'];
  const slug = a['slug'];
  const warehouseId = a['warehouse_id'];
  const spec = parseSpecJson(a['spec']);
  if (typeof title !== 'string' || typeof slug !== 'string' || typeof warehouseId !== 'string' || !spec) {
    return null;
  }
  return {
    title,
    slug,
    warehouseId,
    // Omit when absent so syncAfterSave / applyAgentDraft can preserve an existing description.
    ...(typeof a['description'] === 'string' ? { description: a['description'] } : {}),
    spec,
  };
}

export type { DashboardToolResultSummary };

export function parseDashboardToolResultSummary(content: string | undefined): DashboardToolResultSummary {
  if (!content) return {};
  try {
    const parsed = JSON.parse(content) as Record<string, unknown>;
    const { title, panelCount, panelIds } = parsed;
    return {
      ...(typeof title === 'string' ? { title } : {}),
      ...(typeof panelCount === 'number' ? { panelCount } : {}),
      ...(Array.isArray(panelIds) && panelIds.every((id) => typeof id === 'string') ? { panelIds: panelIds } : {}),
    };
  } catch {
    return {};
  }
}

/**
 * Put the panels in the order the tool reported. Replaying a run of patches over a copy that is already
 * ahead (a mid-turn reload, where the live draft event landed first) yields the right panels but can
 * append a re-added one after a panel it preceded on the server; the reported order makes that replay
 * land where the tool did. A panel whose id the tool did not list is not on the dashboard the tool
 * wrote — the copy was stale (say, a panel removed in the sidebar since) — so it is dropped. A panel
 * without a string id cannot be listed, so it keeps its relative order after the rest.
 */
function orderPanelsLike(panels: unknown[], panelIds: string[] | undefined): unknown[] {
  if (!panelIds) return panels;
  const rank = new Map(panelIds.map((id, index) => [id, index]));
  const idOf = (panel: unknown): string | undefined => {
    const id = typeof panel === 'object' && panel !== null ? (panel as Record<string, unknown>)['id'] : undefined;
    return typeof id === 'string' ? id : undefined;
  };
  const rankOf = (panel: unknown): number => {
    const id = idOf(panel);
    return (id === undefined ? undefined : rank.get(id)) ?? panelIds.length;
  };
  return panels
    .filter((panel) => {
      const id = idOf(panel);
      return id === undefined || rank.has(id);
    })
    .sort((a, b) => rankOf(a) - rankOf(b));
}

/** The call's arguments as the shared resolver reads them; null when a field has the wrong type. */
function dashboardEditCallFromArgs(a: Record<string, unknown>): DashboardEditCall | null {
  const strings: Record<string, string> = {};
  for (const key of ['title', 'description', 'warehouse_id', 'spec'] as const) {
    const value = a[key];
    if (value === undefined) continue;
    if (typeof value !== 'string') return null;
    strings[key] = value;
  }
  const objects = (value: unknown): Record<string, unknown>[] | undefined =>
    Array.isArray(value)
      ? value.filter((v): v is Record<string, unknown> => typeof v === 'object' && v !== null)
      : undefined;
  const removePanelIds = Array.isArray(a['remove_panel_ids'])
    ? a['remove_panel_ids'].filter((id): id is string => typeof id === 'string')
    : undefined;
  return {
    ...strings,
    panels: objects(a['panels']),
    remove_panel_ids: removePanelIds,
    parameters: objects(a['parameters']),
  };
}

/**
 * The dashboard a `preview_dashboard` / `update_dashboard` call produced. A full call with `title` and
 * `warehouse_id` carries it in its arguments. Anything else — a full spec missing those, or a
 * panel-patch call (ACT2-1078) that sends only what changed — is resolved over `base`, this slug's
 * previous snapshot, with the same `resolveDashboardEdit` the tool ran; the tool result does not echo
 * the merged spec. `result` is that call's result summary. Returns null when there is no base to build
 * on, or a patch's base is an empty saved shell (`isArtifactContentEmpty`): a patch over no panels
 * would read as a one-panel dashboard.
 */
export function dashboardDraftFromToolCall(
  args: unknown,
  base: DashboardArtifactSnapshot | undefined,
  result: DashboardToolResultSummary = {},
): DashboardArtifactSnapshot | null {
  const full = previewArgsToDraft(args);
  if (full) return full;
  if (!args || typeof args !== 'object') return null;
  const a = args as Record<string, unknown>;
  const slug = a['slug'];
  if (typeof slug !== 'string' || base?.slug !== slug) return null;
  const call = dashboardEditCallFromArgs(a);
  if (!call) return null;
  // Only a patch needs real panels underneath it; a full spec missing its title still replays over a shell.
  if (call.spec === undefined && isArtifactContentEmpty(base)) return null;

  const resolved = resolveDashboardEdit(
    call,
    () => ({
      // The result's title is what the tool resolved, so it wins over a copy that may be behind.
      title: result.title ?? base.title,
      warehouseId: base.warehouseId,
      description: base.description ?? null,
      spec: base.spec,
    }),
    'no base',
    // A replay over a copy the live draft event already brought up to date must not refuse a removal
    // that already happened.
    { ignoreMissingRemovals: true },
  );
  if (!resolved.ok) return null;
  const spec = parseSpecJson(resolved.spec);
  if (!spec) return null;
  return {
    title: resolved.title,
    slug,
    warehouseId: resolved.warehouseId,
    // Omit when absent so syncAfterSave / applyAgentDraft can preserve an existing description.
    ...(resolved.description === undefined ? {} : { description: resolved.description }),
    spec: resolved.patch
      ? { ...spec, panels: orderPanelsLike(spec.panels, result.panelIds) as DashboardSpec['panels'] }
      : spec,
  };
}

/** The slug a dashboard tool call targeted, readable even when the call failed or was a patch. */
export function dashboardToolCallSlug(args: unknown): string | null {
  if (!args || typeof args !== 'object') return null;
  const slug = (args as Record<string, unknown>)['slug'];
  return typeof slug === 'string' ? slug : null;
}

export function parseDashboardPersistResult(content: string): SavedDashboardRef | null {
  try {
    const parsed = JSON.parse(content) as { id?: string; slug?: string };
    if (typeof parsed.id === 'string' && typeof parsed.slug === 'string') {
      return { id: parsed.id, slug: parsed.slug };
    }
  } catch {
    // ignore
  }
  return null;
}

/** @deprecated Use {@link parseDashboardPersistResult}. */
export const parseSaveDashboardResult = parseDashboardPersistResult;

/** `preview_dashboard` / `update_dashboard` success results include `{ ok: true }`. */
export function isSuccessfulPreviewResult(content: string): boolean {
  try {
    const parsed = JSON.parse(content) as { ok?: boolean };
    return parsed.ok === true;
  } catch {
    return false;
  }
}

export function parseDashboardArtifactUiMetadata(metadata: unknown): DashboardArtifactUiMetadata | null {
  if (!metadata || typeof metadata !== 'object') return null;
  const m = metadata as Record<string, unknown>;
  const slug = m['slug'];
  const title = m['title'];
  const event = m['event'];
  if (typeof slug !== 'string' || typeof title !== 'string') return null;
  if (event !== 'preview_updated' && event !== 'saved' && event !== 'draft_restored') return null;
  const revision = m['revision'];
  return {
    slug,
    title,
    event,
    revision: typeof revision === 'number' ? revision : undefined,
  };
}

/**
 * Rebuild all dashboard artifacts from persisted SSE messages.
 *
 * Per slug, keep the newest successful preview spec. A save only attaches when it
 * appears after that preview (and slug matches) — a later unsaved re-preview clears saved.
 */
export function restoreDashboardArtifactsFromMessages(messages: SSEMessage[]): Record<string, DashboardArtifact> {
  const toolResults = new Map<string, string>();
  for (const msg of messages) {
    if (msg.type === 'tool_result') {
      toolResults.set(msg.id, msg.content);
    }
  }

  interface SlugState {
    snapshot: DashboardArtifactSnapshot;
    lastPreviewIndex: number;
    saved: SavedDashboardRef | null;
  }

  const bySlug = new Map<string, SlugState>();
  // Messages are walked in order, so a patch call replays over the snapshot its slug had just before it.
  const previousSnapshot = (args: unknown): DashboardArtifactSnapshot | undefined => {
    const slug = dashboardToolCallSlug(args);
    return slug === null ? undefined : bySlug.get(slug)?.snapshot;
  };
  const lastSavedIndexBySlug = new Map<string, number>();
  const draftRestoredIndexBySlug = new Map<string, number>();

  for (let i = 0; i < messages.length; i++) {
    const msg = messages[i]!;

    if (msg.type === 'ui_message' && msg.uiType === 'dashboard_artifact') {
      const meta = parseDashboardArtifactUiMetadata(msg.metadata);
      if (meta?.event === 'draft_restored') {
        draftRestoredIndexBySlug.set(meta.slug, i);
      }
      continue;
    }

    if (msg.type !== 'tool_use') continue;

    if (msg.name === DASHBOARD_UPDATE_TOOL) {
      const resultContent = toolResults.get(msg.id);
      if (!resultContent || !isSuccessfulPreviewResult(resultContent)) continue;
      const draft = dashboardDraftFromToolCall(
        msg.args,
        previousSnapshot(msg.args),
        parseDashboardToolResultSummary(resultContent),
      );
      const ref = parseDashboardPersistResult(resultContent);
      if (!ref) continue;
      if (!draft) {
        // A patch with no full call of this slug before it in the transcript (a dashboard first opened
        // from get_dashboard, or saved from the panel) cannot be replayed. Keep the saved ref as an empty
        // saved entry — the shape `hydrateSavedAndOpen` makes — so the dashboard stays in
        // `dashboard_artifacts_in_chat` with its id, and opening it loads the saved row.
        const existing = bySlug.get(ref.slug);
        if (existing && !isArtifactContentEmpty(existing.snapshot)) continue;
        const args = msg.args as Record<string, unknown> | null;
        const argTitle = typeof args?.['title'] === 'string' ? args['title'] : undefined;
        bySlug.set(ref.slug, {
          snapshot: {
            title:
              parseDashboardToolResultSummary(resultContent).title ?? argTitle ?? existing?.snapshot.title ?? ref.slug,
            slug: ref.slug,
            warehouseId: '',
            description: null,
            spec: { parameters: [], panels: [] },
          },
          lastPreviewIndex: i,
          saved: ref,
        });
        lastSavedIndexBySlug.set(ref.slug, i);
        continue;
      }
      bySlug.set(draft.slug, {
        snapshot: draft,
        lastPreviewIndex: i,
        saved: ref,
      });
      lastSavedIndexBySlug.set(draft.slug, i);
      continue;
    }

    if (msg.name === DASHBOARD_PREVIEW_TOOL) {
      const resultContent = toolResults.get(msg.id);
      if (!resultContent || !isSuccessfulPreviewResult(resultContent)) continue;
      const draft = dashboardDraftFromToolCall(
        msg.args,
        previousSnapshot(msg.args),
        parseDashboardToolResultSummary(resultContent),
      );
      if (!draft) continue;
      bySlug.set(draft.slug, {
        snapshot: draft,
        lastPreviewIndex: i,
        saved: null,
      });
      continue;
    }

    if (msg.name === DASHBOARD_SAVE_TOOL) {
      const content = toolResults.get(msg.id);
      if (!content) continue;
      const ref = parseDashboardPersistResult(content);
      if (!ref) continue;

      const draft = previewArgsToDraft(msg.args);
      const state = bySlug.get(ref.slug);

      // save_dashboard-only (no prior preview): seed from tool args when present.
      if (!state && draft?.slug === ref.slug) {
        bySlug.set(ref.slug, {
          snapshot: draft,
          lastPreviewIndex: -1,
          saved: ref,
        });
        lastSavedIndexBySlug.set(ref.slug, i);
        continue;
      }

      if (!state || i <= state.lastPreviewIndex) continue;

      const args = msg.args as Record<string, unknown> | null;
      const saveSlug = typeof args?.['slug'] === 'string' ? args['slug'] : null;
      if (saveSlug !== null && saveSlug !== ref.slug) continue;

      state.saved = ref;
      lastSavedIndexBySlug.set(ref.slug, i);
    }
  }

  const artifacts: Record<string, DashboardArtifact> = {};
  for (const [slug, state] of bySlug) {
    const restoredAt = draftRestoredIndexBySlug.get(slug);
    const savedAt = lastSavedIndexBySlug.get(slug) ?? -1;
    const saved = restoredAt !== undefined && restoredAt >= savedAt ? null : state.saved;
    artifacts[slug] = {
      ...state.snapshot,
      saved,
      patchSeq: 0,
      lastPersistedPatchSeq: 0,
    };
  }
  return artifacts;
}
